import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Home as HomeIcon,
  Replay as RetryIcon
} from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../config';
import { useLanguageContext } from '../contexts/LanguageContext';

interface PaymentResult {
  paymentKey: string;
  orderId: string;
  amount: number;
  orderName: string;
  method: string;
  requestedAt: string;
  approvedAt: string;
  status: string;
  /** Eximbay 결제 시 실제 통화 (없으면 기존 토스 KRW/센트 표기 로직 사용) */
  currency?: string;
}

interface EximbayResultResponse {
  success: boolean;
  donation?: {
    order_id: string;
    status: string;
    amount: number;
    currency: string;
    transaction_id: string | null;
    payment_method: string | null;
    rescode: string | null;
    resmsg: string | null;
    worship_id: string | null;
    approved_at: string | null;
    created_at: string | null;
  };
  error?: string;
}

const EXIMBAY_POLL_ATTEMPTS = 4;
const EXIMBAY_POLL_INTERVAL_MS = 1500;
const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const PaymentResultPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, currentLanguage } = useLanguageContext();
  const [isLoading, setIsLoading] = useState(true);
  const [paymentResult, setPaymentResult] = useState<PaymentResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  // 통화 포맷팅 함수
  const formatCurrency = (value: string | number, currency?: string) => {
    // Eximbay: 실제 통화 단위 금액 (USD 5.00 / KRW 5000)
    if (currency) {
      const num = typeof value === 'string' ? parseFloat(value) : value;
      if (currency === 'KRW') return `${Math.round(num).toLocaleString()}원`;
      return `US$ ${num.toFixed(2)}`;
    }
    const numValue = typeof value === 'string' ? parseInt(value) : value;
    if (currentLanguage === 'en') {
      return `US$ ${(numValue / 100).toFixed(2)}`;
    } else {
      return `${numValue.toLocaleString()}원`;
    }
  };

  const paymentKey = searchParams.get('paymentKey');
  const orderId = searchParams.get('orderId');
  const amount = searchParams.get('amount');
  const code = searchParams.get('code');
  const message = searchParams.get('message');
  const worshipId = searchParams.get('worship_id');
  // Eximbay 복귀 파라미터 (백엔드 /api/eximbay-payment/return 이 리다이렉트)
  const provider = searchParams.get('provider');
  const eximbayOrderId = searchParams.get('order_id');
  const eximbayRescode = searchParams.get('rescode');
  const eximbayResmsg = searchParams.get('resmsg');

  useEffect(() => {
    const processEximbayResult = async () => {
      if (!eximbayOrderId) {
        setError(t('payment.invalidInfo'));
        return;
      }

      // status_url 통지가 return 보다 늦게 도착할 수 있으므로 pending 이면 잠시 재조회
      let donation: EximbayResultResponse['donation'] | undefined;
      for (let attempt = 0; attempt < EXIMBAY_POLL_ATTEMPTS; attempt += 1) {
        const response = await axios.get<EximbayResultResponse>(
          getApiUrl(`eximbay-payment/result/${encodeURIComponent(eximbayOrderId)}`)
        );
        donation = response.data?.donation;
        if (!donation || donation.status !== 'pending') break;
        await sleep(EXIMBAY_POLL_INTERVAL_MS);
      }

      if (!donation) {
        setError(t('payment.invalidInfo'));
        return;
      }

      if (donation.status === 'completed') {
        setPaymentResult({
          paymentKey: donation.transaction_id || '',
          orderId: donation.order_id,
          amount: donation.amount,
          orderName: '',
          method: donation.payment_method || 'Eximbay',
          requestedAt: donation.created_at || '',
          approvedAt: donation.approved_at || donation.created_at || '',
          status: donation.status,
          currency: donation.currency
        });
        setIsSuccess(true);
        return;
      }

      if (donation.status === 'pending') {
        setError(t('payment.pendingConfirmation'));
        return;
      }

      const failureMessage =
        message ||
        donation.resmsg ||
        eximbayResmsg ||
        (eximbayRescode && eximbayRescode !== '0000' ? `${t('payment.failedDetail')} (${eximbayRescode})` : null) ||
        t('payment.approvalFailed');
      setError(failureMessage);
    };

    const processPaymentResult = async () => {
      try {
        setIsLoading(true);

        // Eximbay 결제 결과 처리
        if (provider === 'eximbay') {
          await processEximbayResult();
          return;
        }

        // 결제 실패한 경우
        if (code || message) {
          setError(message || t('payment.failedDetail'));
          setIsLoading(false);
          return;
        }

        // 필수 파라미터 확인
        if (!paymentKey || !orderId || !amount) {
          setError(t('payment.invalidInfo'));
          setIsLoading(false);
          return;
        }

        // 백엔드에 결제 승인 요청
        const token = localStorage.getItem('token');
        if (!token) {
          setError(t('payment.loginRequired'));
          setIsLoading(false);
          return;
        }

        const response = await axios.post(
          getApiUrl('toss-payment/confirm'),
          {
            paymentKey,
            orderId,
            amount: parseInt(amount),
            worship_id: worshipId
          },
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.data.success) {
          setPaymentResult(response.data.payment);
          setIsSuccess(true);
        } else {
          setError(response.data.error || t('payment.approvalFailed'));
        }

      } catch (error: any) {
        console.error('결제 결과 처리 오류:', error);
        setError(
          error.response?.data?.error || 
          error.message || 
          t('payment.processingError')
        );
      } finally {
        setIsLoading(false);
      }
    };

    processPaymentResult();
  }, [paymentKey, orderId, amount, code, message, worshipId, t, provider, eximbayOrderId, eximbayRescode, eximbayResmsg]);

  const handleGoHome = () => {
    navigate('/');
  };

  const handleGoToWorship = () => {
    if (worshipId) {
      navigate(`/worship?id=${worshipId}`);
    } else {
      navigate('/');
    }
  };

  const handleRetry = () => {
    if (worshipId) {
      navigate(`/donation?worship_id=${worshipId}`);
    } else {
      navigate('/donation');
    }
  };

  if (isLoading) {
    return (
      <Box 
        sx={{ 
          minHeight: '100vh',
          py: 4
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={0}
            sx={{
              p: 4,
              textAlign: 'center',
              backgroundColor: 'transparent',
              border: '2px solid white',
              borderRadius: 4,
              color: 'white',
              '& .MuiTypography-root': { color: 'white' }
            }}
          >
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            {t('payment.loading')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('payment.pleaseWait')}
          </Typography>
        </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        minHeight: '100vh',
        py: 4
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={0}
          sx={{
            p: 4,
            backgroundColor: 'transparent',
            border: '2px solid white',
            borderRadius: 4,
            color: 'white',
            '& .MuiTypography-root': { color: 'white' },
            '& .MuiAlert-root .MuiTypography-root': { color: 'inherit' },
            '& .MuiDivider-root': { borderColor: 'rgba(255, 255, 255, 0.3)' }
          }}
        >
        {/* 결제 성공 */}
        {isSuccess && paymentResult && (
          <>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <SuccessIcon 
                sx={{ 
                  fontSize: 80, 
                  color: 'success.main',
                  mb: 2 
                }} 
              />
              <Typography variant="h4" gutterBottom color="success.main">
                {t('payment.success')}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {t('payment.donationSuccess')}
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t('payment.info')}
              </Typography>
              <Box sx={{ display: 'grid', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('payment.amount')}:
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {formatCurrency(paymentResult.amount, paymentResult.currency)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('payment.method')}:
                  </Typography>
                  <Typography variant="body2">
                    {paymentResult.method}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('payment.orderNumber')}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                    {paymentResult.orderId}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('payment.time')}:
                  </Typography>
                  <Typography variant="body2">
                    {new Date(paymentResult.approvedAt).toLocaleString(currentLanguage === 'ko' ? 'ko-KR' : 'en-US')}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                startIcon={<HomeIcon />}
                onClick={handleGoToWorship}
              >
                {worshipId ? 
                  t('payment.returnToWorship') : 
                  t('payment.returnHome')
                }
              </Button>
            </Box>
          </>
        )}

        {/* 결제 실패 */}
        {error && (
          <>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <ErrorIcon 
                sx={{ 
                  fontSize: 80, 
                  color: 'error.main',
                  mb: 2 
                }} 
              />
              <Typography variant="h4" gutterBottom color="error.main">
                {t('payment.failed')}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                {t('payment.failedMessage')}
              </Typography>
            </Box>

            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<HomeIcon />}
                onClick={handleGoHome}
                sx={{ color: 'white', borderColor: 'rgba(255, 255, 255, 0.6)' }}
              >
                {t('payment.returnHome')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                startIcon={<RetryIcon />}
                onClick={handleRetry}
              >
                {t('payment.retry')}
              </Button>
            </Box>
          </>
        )}

        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            {t('payment.customerService')}
          </Typography>
        </Box>
      </Paper>
      </Container>
    </Box>
  );
};

export default PaymentResultPage;